import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Loader from "@components/Loader";
import {
  useServiceConfigQuery,
  useSaveServiceConfigMutation,
  useServiceBookingsQuery
} from "@queries/website";
import "@styles/opencart-admin.css";

const todayISO = () => new Date().toISOString().slice(0, 10);

const TYPE_LABELS = {
  carpets: "Carpets",
  furniture: "Furniture",
  car_cleaning: "Car Cleaning",
  housekeeping: "Housekeeping"
};

const hm = (t) => (t ? String(t).substring(0, 5) : "");

// minutes since midnight from "HH:MM"
const toMin = (t) => {
  const [h, m] = hm(t).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

export default function ServiceSchedulingPage() {
  const queryClient = useQueryClient();

  const { data: configs, isLoading, isError, error } = useServiceConfigQuery();
  const saveMutation = useSaveServiceConfigMutation();

  // editable copy of each config row, keyed by order_type
  const [drafts, setDrafts] = React.useState({});
  const [date, setDate] = React.useState(todayISO());

  const {
    data: bookingsData,
    isFetching: bookingsFetching
  } = useServiceBookingsQuery(date, "");

  React.useEffect(() => {
    if (!configs) return;
    const next = {};
    configs.forEach((c) => {
      next[c.order_type] = {
        teams: c.teams,
        day_start: hm(c.day_start),
        day_end: hm(c.day_end),
        min_hours: c.min_hours,
        slot_step: c.slot_step,
        buffer_minutes: c.buffer_minutes
      };
    });
    setDrafts(next);
  }, [configs]);

  function setDraft(type, field, value) {
    setDrafts((d) => ({ ...d, [type]: { ...d[type], [field]: value } }));
  }

  async function handleSave(type) {
    const d = drafts[type];
    try {
      await saveMutation.mutateAsync({
        type,
        payload: {
          teams: Number(d.teams),
          day_start: d.day_start,
          day_end: d.day_end,
          min_hours: Number(d.min_hours),
          slot_step: Number(d.slot_step),
          buffer_minutes: Number(d.buffer_minutes)
        }
      });
      queryClient.invalidateQueries({ queryKey: ["website", "service-config"] });
      queryClient.invalidateQueries({ queryKey: ["website", "service-bookings"] });
    } catch (err) {
      const errors = err?.response?.data?.errors;
      alert(
        errors
          ? Object.values(errors).flat().join("\n")
          : err?.response?.data?.message || "Failed to save config"
      );
    }
  }

  const bookingsByType = bookingsData?.bookings || {};

  function dayBar(booking, config) {
    // simple visual bar for the booking inside the working window
    const start = toMin(config?.day_start || "08:00");
    const end = toMin(config?.day_end || "20:00");
    const span = Math.max(1, end - start);
    const left = ((toMin(booking.order_start_time) - start) / span) * 100;
    const width =
      ((toMin(booking.order_end_time) - toMin(booking.order_start_time)) / span) * 100;
    return (
      <div style={{ position: "relative", height: 18, background: "#eef2f7", borderRadius: 4 }}>
        <div
          style={{
            position: "absolute",
            left: `${Math.max(0, Math.min(100, left))}%`,
            width: `${Math.max(2, Math.min(100, width))}%`,
            top: 2,
            bottom: 2,
            background: "#2563eb",
            borderRadius: 4
          }}
          title={`${hm(booking.order_start_time)}–${hm(booking.order_end_time)}`}
        />
      </div>
    );
  }

  return (
    <div className="oc-content">
      {(isLoading || bookingsFetching) && <Loader />}

      <div className="oc-header">
        <h1>Service Scheduling</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>Service Scheduling</span>
        </nav>
      </div>

      {isError && (
        <div className="oc-card" style={{ color: "#c00" }}>
          Error: {error?.message || "Unknown error"}
        </div>
      )}

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Team Capacity & Working Hours</strong>
            <div style={{ fontSize: 12, color: "#777" }}>
              Per on-site order type. A time slot is bookable while overlapping booked teams &lt;
              teams; the travel buffer extends each booking's busy window.
            </div>
          </div>
        </div>

        <table className="oc-table">
          <thead>
            <tr>
              <th>Order Type</th>
              <th style={{ width: 90 }}>Teams</th>
              <th style={{ width: 110 }}>Day Start</th>
              <th style={{ width: 110 }}>Day End</th>
              <th style={{ width: 110 }}>Min Hours</th>
              <th style={{ width: 110 }}>Step (min)</th>
              <th style={{ width: 120 }}>Buffer (min)</th>
              <th style={{ width: 100 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {(configs || []).map((c) => {
              const d = drafts[c.order_type];
              if (!d) return null;
              return (
                <tr key={c.order_type}>
                  <td>
                    <strong>{TYPE_LABELS[c.order_type] || c.order_type}</strong>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      className="oc-input"
                      value={d.teams}
                      onChange={(e) => setDraft(c.order_type, "teams", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      className="oc-input"
                      value={d.day_start}
                      onChange={(e) => setDraft(c.order_type, "day_start", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      className="oc-input"
                      value={d.day_end}
                      onChange={(e) => setDraft(c.order_type, "day_end", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      className="oc-input"
                      value={d.min_hours}
                      onChange={(e) => setDraft(c.order_type, "min_hours", e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      className="oc-input"
                      value={d.slot_step}
                      onChange={(e) => setDraft(c.order_type, "slot_step", e.target.value)}
                    >
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="60">60</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      max="240"
                      className="oc-input"
                      value={d.buffer_minutes}
                      onChange={(e) => setDraft(c.order_type, "buffer_minutes", e.target.value)}
                    />
                  </td>
                  <td>
                    <button
                      className="oc-btn primary"
                      disabled={saveMutation.isPending}
                      onClick={() => handleSave(c.order_type)}
                    >
                      Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: 10, fontSize: 12, color: "#777" }}>
          Pickup / drop-off (laundry) capacity is per time slot — manage it under{" "}
          <Link to="/logistics/time-slots">Logistics › Time Slots</Link>.
        </div>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Day Bookings</strong>
            <div style={{ fontSize: 12, color: "#777" }}>
              On-site jobs for the selected day, grouped by order type.
            </div>
          </div>
          <div>
            <input
              type="date"
              className="oc-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        {(configs || []).map((c) => {
          const list = bookingsByType[c.order_type] || [];
          return (
            <div key={c.order_type} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <strong>{TYPE_LABELS[c.order_type] || c.order_type}</strong>
                <span style={{ fontSize: 12, color: list.length >= c.teams ? "#b91c1c" : "#15803d" }}>
                  {list.length} booking{list.length === 1 ? "" : "s"} · {c.teams} team
                  {c.teams === 1 ? "" : "s"} · window {hm(c.day_start)}–{hm(c.day_end)}
                </span>
              </div>
              {list.length === 0 ? (
                <div style={{ fontSize: 13, color: "#999", padding: "4px 0 8px" }}>
                  No bookings.
                </div>
              ) : (
                <table className="oc-table">
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>Order</th>
                      <th>Customer</th>
                      <th style={{ width: 130 }}>Time</th>
                      <th style={{ width: 220 }}>Timeline</th>
                      <th style={{ width: 110 }}>Status</th>
                      <th style={{ width: 90 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((b) => (
                      <tr key={b.order_id}>
                        <td>
                          <Link to={`/orders/${b.order_id}`}>#{b.order_id}</Link>
                        </td>
                        <td>
                          {b.name || "—"}
                          <div style={{ fontSize: 12, color: "#777" }}>{b.telephone}</div>
                        </td>
                        <td>
                          {hm(b.order_start_time)}–{hm(b.order_end_time)}
                        </td>
                        <td>{dayBar(b, c)}</td>
                        <td>{b.order_status}</td>
                        <td>{b.total != null ? Number(b.total).toFixed(2) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
